import { Box, Stack, Typography } from '@mui/material';
import { Flight, Schedule } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import type { BookingTripSummary } from '../bookingTripSummary';
import { CarBookingSummary } from './CarBookingSummary';
import { transferLuggageSummary } from '../transferLuggage';

export function BookingTripSummaryView({ summary }: { summary: BookingTripSummary | null }) {
  const { t } = useTranslation();
  if (!summary) return null;
  if (summary.kind === 'car') {
    return <CarBookingSummary {...summary} />;
  }
  if (summary.kind === 'lines') {
    return (
      <Box bgcolor="grey.100" borderRadius={1} px={2} py={1.5}>
        {summary.lines.map((line, index) => (
          <Typography key={index} variant="body2">
            {line}
          </Typography>
        ))}
      </Box>
    );
  }
  return (
    <Stack spacing={1.25}>
      {summary.requestedLuggage && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.transfers.luggage.requested')}: {transferLuggageSummary(summary.requestedLuggage, t)}
        </Typography>
      )}
      {summary.legs.map((leg) => (
        <Box key={leg.direction} border={1} borderColor="divider" borderRadius={2} p={1.5}>
          <Typography variant="subtitle2" gutterBottom>
            {t(leg.direction === 'return' ? 'travelServices.booking.return' : 'travelServices.booking.outbound')}
          </Typography>
          {(leg.date || leg.time || leg.flightNumber) && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
              {leg.timeKind === 'flight' ? (
                <Flight fontSize="small" color="action" />
              ) : (
                <Schedule fontSize="small" color="action" />
              )}
              {leg.date && (
                <Typography variant="body2" fontWeight={600}>
                  {leg.date}
                </Typography>
              )}
              {leg.time && (
                <Typography variant="body2">
                  {t(
                    leg.timeKind === 'flight'
                      ? 'travelServices.transfers.summaryFlightTime'
                      : 'travelServices.transfers.summaryServiceTime',
                    { time: leg.time },
                  )}
                </Typography>
              )}
              {leg.flightNumber && (
                <Typography variant="body2">
                  {t('travelServices.transfers.summaryFlightNumber', { flight: leg.flightNumber })}
                </Typography>
              )}
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.transfers.summaryFrom')}
            </Typography>
            <Typography variant="body2">{leg.from}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.transfers.summaryTo')}
            </Typography>
            <Typography variant="body2">{leg.to}</Typography>
          </Stack>
        </Box>
      ))}
      {summary.resolvedRoute && (
        <Box bgcolor="info.50" borderRadius={1} px={2} py={1.25}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('travelServices.transfers.providerResolvedRoute')}
          </Typography>
          <Typography variant="body2">
            {summary.resolvedRoute.from} → {summary.resolvedRoute.to}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
